import { Link } from '@/components/Elements/Link';
import { FeatureKeys } from '@/config';
import { useFilterConditionAtom } from '@/features/analyze/atoms/filterCondition.atom';
import { FacetSelectField } from '@/features/analyze/components/facet/FacetSelectField';
import { DateRangePicker } from '@/features/analyze/components/Filter/DateRangePicker';
import { SavedFiltersList } from '@/features/analyze/components/Filter/SavedFilter/SavedFiltersList';
import { SaveFilterButton } from '@/features/analyze/components/Filter/SavedFilter/SaveFilterButton';
import { Z_INDEX } from '@/features/analyze/constants';
import { CanAccess } from '@/providers/CanAccess';
import { rwColors } from '@/styles/colors';
import {
  BookmarkIcon,
  BuildingOffice2Icon,
  CubeIcon,
  GlobeAltIcon,
  HashtagIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DownloadArticleDialogButton } from 'src/features/analyze/components/DownloadArticle';
import { OpenTagViewButton } from '@/features/connect-dots/components/OpenTagViewButton';

export const Filter = () => {
  const [filterCondition, setFilterCondition] = useFilterConditionAtom();
  const { t, i18n } = useTranslation();

  return (
    <div className="relative flex h-full w-80 shrink-0 flex-col bg-rw-light-gray text-white">
      {/* スクロール可能なコンテンツエリア */}
      <div className="flex flex-1 flex-col gap-y-6 overflow-y-auto pt-6">
        <h2
          translate="no"
          className="px-8 text-2xl font-semibold text-rw-black"
        >
          RuleWatch
        </h2>

        <div className="mr-10 flex flex-col gap-2">
          <Link
            to="/bookmark/list"
            underline="none"
            className="flex items-center gap-3 rounded-r-full bg-rw-white py-1 pl-8 font-medium text-rw-dark-gray backdrop-blur-2xl"
          >
            <BookmarkIcon className="size-4" />
            <span className="flex-1 text-xl">{t('ブックマーク一覧')}</span>
          </Link>
          <Link
            to="/organizations"
            underline="none"
            className="flex items-center gap-3 rounded-r-full bg-rw-white/80 py-1 pl-8 font-medium text-rw-dark-gray backdrop-blur-2xl"
          >
            <BuildingOffice2Icon className="size-4" />
            <span className="flex-1 text-xl">{t('情報源一覧')}</span>
          </Link>
        </div>

        {/* Saved Filters Section */}
        <CanAccess featureKey={FeatureKeys.saveFilters}>
          <SavedFiltersList className="mx-8 p-1.5" />
        </CanAccess>

        <div className="mx-8 flex flex-col gap-y-2 rounded-tr-2xl bg-rw-white p-2 text-rw-dark-gray">
          <div className="flex items-center justify-between">
            <div className="text-xl font-medium">Filter</div>
            <CanAccess featureKey={FeatureKeys.saveFilters}>
              <SaveFilterButton />
            </CanAccess>
            <OpenTagViewButton className="text-xs" />
          </div>
          <hr className="my-0.5 h-[3px] bg-rw-light-gray" />

          <DateRangePicker
            label={t('公開日時')}
            locale={i18n.language}
            selectedStart={filterCondition.publishedStartAt}
            selectedEnd={filterCondition.publishedEndAt}
            onStartChange={(date: Date | null) => {
              setFilterCondition((prev) => ({
                ...prev,
                publishedStartAt: date,
              }));
            }}
            onEndChange={(date: Date | null) => {
              setFilterCondition((prev) => ({ ...prev, publishedEndAt: date }));
            }}
          />

          <FacetSelectField
            facetKey="themes"
            modes={['and', 'or']}
            label={t('テーマ')}
            labelColor={rwColors.sub}
            IconComponent={HashtagIcon}
            labelClassName="text-rw-sub"
          />

          <FacetSelectField
            label={t('国グループ')}
            modes={['or']}
            labelColor={rwColors['light-gray']}
            backgroundColor={rwColors['dark-gray']}
            facetKey="countryTags"
            labelClassName="text-rw-dark-gray"
          />

          <FacetSelectField
            label={t('国・地域')}
            modes={['or', 'not']}
            labelColor={rwColors.green}
            facetKey="countries"
            IconComponent={GlobeAltIcon}
            labelClassName="text-rw-green"
          />

          <FacetSelectField
            label={t('組織タイプ')}
            modes={['or']}
            labelColor={rwColors.green}
            facetKey="organizationTypes"
            IconComponent={CubeIcon}
            labelClassName="text-rw-green"
          />

          <FacetSelectField
            label={t('タグ')}
            modes={['and', 'or', 'not']}
            labelColor={rwColors.black}
            facetKey="topicTags"
            IconComponent={TagIcon}
            labelClassName="text-rw-black"
          />
        </div>
      </div>

      <CanAccess featureKey={FeatureKeys.downloadArticles}>
        {/* 下部固定ダウンロードボタンをstickyで実装 */}
        <div
          className="sticky bottom-0 h-24 bg-rw-light-gray p-6"
          style={{ zIndex: Z_INDEX.FILTER_DL_BUTTON }}
        >
          <DownloadArticleDialogButton />
        </div>
      </CanAccess>
    </div>
  );
};
